import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, api, formatApiErrorDetail, getToken } from "../lib/api";
import RatingModal from "./RatingModal";

const COLUMNS = ["submitted", "reviewing", "interviewing", "shortlisted", "hired", "rejected"];
const REJECTION_REASONS = ["not_a_fit", "too_expensive", "unavailable", "other"];

const NEXT_STATUSES = {
  submitted: ["reviewing", "interviewing", "shortlisted", "hired", "rejected"],
  reviewing: ["interviewing", "shortlisted", "hired", "rejected"],
  interviewing: ["shortlisted", "hired", "rejected"],
  shortlisted: ["hired", "rejected"],
};

function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function SubmissionKanban({ missionId, mission }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [ratedIds, setRatedIds] = useState({}); // {submission_id: overall}
  const [ratingTarget, setRatingTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectForId, setRejectForId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("not_a_fit");
  const [rejectionNote, setRejectionNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/missions/${missionId}/submissions`);
      setItems(data);
      // Fetch ratings for terminal submissions so we can show "Rated" pill.
      const terminals = data.filter((s) => ["hired", "rejected", "withdrawn"].includes(s.status));
      const rated = {};
      await Promise.all(terminals.map(async (s) => {
        try {
          const r = await api.get(`/submissions/${s.id}/ratings`, { skipPaywallRedirect: true });
          const mine = r.data.find((x) => x.direction === "employer_to_agency");
          if (mine) rated[s.id] = mine.overall;
        } catch (_) { /* ignore */ }
      }));
      setRatedIds(rated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [missionId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const transition = async (id, new_status, extras = {}) => {
    if (new_status === "hired") {
      if (!window.confirm(t("submissions.confirm_hire"))) return;
    }
    setBusyId(id);
    try {
      await api.post(`/submissions/${id}/status`, { new_status, ...extras });
      toast.success(t("submissions.status_updated"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setBusyId(null);
      setRejectForId(null);
      setRejectionNote("");
    }
  };

  const openThread = async (submissionId) => {
    try {
      const { data } = await api.post(`/submissions/${submissionId}/thread`);
      navigate(`/messages/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  const openCv = async (submissionId) => {
    try {
      const res = await fetch(`${API}/submissions/${submissionId}/cv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 403) {
        toast.error(t("submissions.cv_forbidden"));
        return;
      }
      if (res.status === 404) {
        toast.error(t("submissions.cv_not_available"));
        return;
      }
      if (!res.ok) {
        toast.error(t("common.error_generic"));
        return;
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (_) {
      toast.error(t("common.error_generic"));
    }
  };

  const visible = showWithdrawn ? items : items.filter((s) => s.status !== "withdrawn");
  const cols = COLUMNS.map((k) => ({ key: k, items: visible.filter((s) => s.status === k) }));

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="submissions-kanban">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {t("submissions.count", { n: items.length })}
        </p>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={showWithdrawn} onChange={(e) => setShowWithdrawn(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-testid="show-withdrawn-toggle" />
          {t("submissions.show_withdrawn")}
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        {cols.map((c) => (
          <div key={c.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`kanban-col-${c.key}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                {t(`submissions.status_${c.key}`)}
              </span>
              <span className="rounded-full bg-white px-1.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">{c.items.length}</span>
            </div>
            <ul className="space-y-2">
              {c.items.map((s) => (
                <li key={s.id} className="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm" data-testid={`sub-card-${s.id}`}>
                  <div className="flex items-center gap-1.5">
                    <p className="flex-1 font-semibold text-slate-900" data-testid={`sub-card-name-${s.id}`}>
                      {s.consultant.display_name}
                    </p>
                    {!s.consultant.unlocked && s.consultant.redact_identity && (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 ring-1 ring-slate-200" title={t("submissions.locked_hint")} data-testid={`sub-card-locked-${s.id}`}>🔒</span>
                    )}
                    {s.consultant.unlocked && s.consultant.real_name && (
                      <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-100" data-testid={`sub-card-unlocked-${s.id}`}>
                        {t("submissions.unlocked")}
                      </span>
                    )}
                  </div>
                  {s.consultant.real_name && (
                    <p className="mt-0.5 text-[11px] text-slate-500">{s.consultant.real_name}</p>
                  )}
                  <p className="mt-0.5 text-slate-600">{s.consultant.title}</p>
                  <p className="mt-0.5 text-slate-500">
                    {s.submitting_agency.name} · {s.proposed_tjm}/day · {fmtDateShort(s.submitted_at)}
                  </p>
                  {s.consultant.skills?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.consultant.skills.slice(0, 3).map((k) => (
                        <span key={k} className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700">{k}</span>
                      ))}
                    </div>
                  )}
                  {s.status === "rejected" && s.rejection_reason && (
                    <p className="mt-1 text-[10px] italic text-rose-700">{t(`submissions.reason_${s.rejection_reason}`)}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button onClick={() => openCv(s.id)} className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50" data-testid={`sub-cv-${s.id}`}>
                      CV
                    </button>
                    <button onClick={() => openThread(s.id)} className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50" data-testid={`sub-msg-${s.id}`}>
                      {t("submissions.message")}
                    </button>
                    {NEXT_STATUSES[s.status] && (
                      <select
                        disabled={busyId === s.id}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (!v) return;
                          if (v === "rejected") setRejectForId(s.id);
                          else transition(s.id, v);
                          e.target.value = "";
                        }}
                        className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-700"
                        data-testid={`sub-move-${s.id}`}
                      >
                        <option value="">{t("submissions.move_to")}</option>
                        {NEXT_STATUSES[s.status].map((k) => (
                          <option key={k} value={k}>{t(`submissions.status_${k}`)}</option>
                        ))}
                      </select>
                    )}
                    {["hired", "rejected", "withdrawn"].includes(s.status) && (
                      ratedIds[s.id] ? (
                        <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`sub-rated-${s.id}`}>★ {ratedIds[s.id]}</span>
                      ) : (
                        <button
                          onClick={() => setRatingTarget({ id: s.id, ratee_name: s.consultant_display_name || (mission && mission.consultant_name) || t("rating.the_agency") })}
                          className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-100"
                          data-testid={`sub-rate-${s.id}`}
                        >
                          {t("rating.rate_button")}
                        </button>
                      )
                    )}
                  </div>
                  {rejectForId === s.id && (
                    <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2" data-testid={`sub-reject-picker-${s.id}`}>
                      <label className="block text-[10px] font-semibold text-rose-800">{t("submissions.rejection_reason")}</label>
                      <select value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="mt-1 w-full rounded border border-rose-200 bg-white px-1.5 py-0.5 text-[10px]" data-testid={`sub-reject-reason-${s.id}`}>
                        {REJECTION_REASONS.map((r) => (<option key={r} value={r}>{t(`submissions.reason_${r}`)}</option>))}
                      </select>
                      <textarea rows={2} value={rejectionNote} onChange={(e) => setRejectionNote(e.target.value)} placeholder={t("submissions.rejection_note_placeholder")} className="mt-1 w-full rounded border border-rose-200 bg-white px-1.5 py-0.5 text-[10px]" data-testid={`sub-reject-note-${s.id}`} />
                      <div className="mt-1 flex gap-1">
                        <button onClick={() => setRejectForId(null)} className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700">{t("common.cancel")}</button>
                        <button onClick={() => transition(s.id, "rejected", { rejection_reason: rejectionReason, rejection_note: rejectionNote })} className="rounded bg-rose-600 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={`sub-reject-confirm-${s.id}`}>
                          {t("submissions.reject_confirm")}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
              {c.items.length === 0 && (
                <li className="text-center text-xs text-slate-400">—</li>
              )}
            </ul>
          </div>
        ))}
      </div>
      {ratingTarget && (
        <RatingModal
          submissionId={ratingTarget.id}
          direction="employer_to_agency"
          ratee_name={ratingTarget.ratee_name}
          onClose={() => setRatingTarget(null)}
          onSubmitted={() => load()}
        />
      )}
    </div>
  );
}
